import React, { useEffect } from "react";
import { IonIcon, IonPage, IonSpinner } from "@ionic/react";
import { useIonRouter } from "@ionic/react";
import {
  chevronForwardOutline,
  informationCircleOutline,
} from "ionicons/icons";
import Header from "../../../components/Header";
import biwazeCough from "../../../assets/icons/biwazeCough.png";
import biwazeClear from "../../../assets/icons/BiWazeClear.jpg";
import { useBLE } from "../../../hooks/useBle";
import { ScanResult } from "@capacitor-community/bluetooth-le";
import { RequestMessageType } from "../../../contexts/types";
import { Capacitor } from "@capacitor/core";

const BluetoothDevicesPage = () => {
  const {
    device,
    connectToDevice,
    connectToPulseOximeterDevice,
    isConnected,
    devices,
    // New pulse oximeter state variables
    isPulseOximeterConnected,
    pulseOximeterDevice,
    pulseOximeterDevices,
    connectionStatus,
    DeviceType,
    getDeviceType,
    openModal,
    parseDeviceString,
    serialNumber,
    askForSelectiveData,
    findAndConnectToDeviceWeb,
  } = useBLE();

  const router = useIonRouter();

  const handleDeviceAction = async (
    deviceData: ScanResult,
    action: "connected" | "connect" | "disconnect" | "connectFailed",
    deviceType: "therapy" | "pulseOximeter" = "therapy"
  ) => {
    console.log(`${action} ${deviceType} device:`, deviceData.device.deviceId);

    if (action === "connect") {
      let connectionResult;

      if (deviceType === "therapy") {
        connectionResult = await connectToDevice(
          deviceData,
          isConnected && device && device.device.deviceId
            ? device.device.deviceId
            : ""
        );
      } else {
        // Use the new pulse oximeter connection function
        connectionResult = await connectToPulseOximeterDevice(deviceData);
      }

      // console.log("Connection result:", connectionResult);
      if (connectionResult.status === 1) {
        openModal(
          deviceType === "pulseOximeter"
            ? "connectedPulseOximeter"
            : "connected",
          deviceData
        );
      } else {
        // openModal("connectFailed", deviceData, connectionResult.message);
      }
    } else {
      openModal(action, deviceData);
    }
  };

  const handleConnectedDeviceDisconnect = (pulseDevice = false) => {
    if (device || pulseOximeterDevice) {
      router.push(
        pulseDevice ? "/remote/pulseOximeterInfo" : "/remote/deviceInfo",
        "forward",
        "replace"
      );
    }
  };

  const DeviceIcon = ({ deviceType }: { deviceType?: string }) => (
    <img
      src={
        deviceType === "Pulse Oximeter"
          ? "https://www.nonin.com/wp-content/uploads/beans/images/Product-Image-PNG-3230_Front-angled-left-0a766af.jpg"
          : deviceType === "BiWaze® Clear"
          ? biwazeClear
          : biwazeCough
      }
      className="w-8 h-8 text-blue-400 flex-shrink-0"
      alt={deviceType || "Device"}
    />
  );

  const DeviceItem = ({
    device: scanDevice,
    section,
    deviceCategory,
  }: {
    device: ScanResult;
    section: string;
    deviceCategory: "therapy" | "pulseOximeter";
  }) => {
    const { isConnectClicked, isPulseOximeterConnectClicked } = useBLE();
    const deviceTypeEnum = getDeviceType(scanDevice.device.name || "");
    const isTherapyDevice = deviceTypeEnum === DeviceType.THERAPY;
    const isPulseOximeterDevice = deviceTypeEnum === DeviceType.PULSE_OXIMETER;

    // Check if this specific device is connected
    const isThisDeviceConnected =
      (isTherapyDevice &&
        isConnected &&
        device &&
        scanDevice.device.deviceId === device.device.deviceId) ||
      (isPulseOximeterDevice &&
        isPulseOximeterConnected &&
        pulseOximeterDevice &&
        scanDevice.device.deviceId === pulseOximeterDevice.device.deviceId);

    const getDeviceDisplayName = () => {
      if (isTherapyDevice && scanDevice.device.name) {
        return parseDeviceString(scanDevice.device.name).deviceType;
      }
      // For pulse oximeters, show the actual device name
      return scanDevice.device.name || "Pulse Oximeter";
    };

    const getDeviceSerial = () => {
      if (isTherapyDevice && scanDevice.device.name) {
        return parseDeviceString(scanDevice.device.name).serialNumber;
      }
      // For pulse oximeters, show device ID or a portion of it
      return scanDevice.device.deviceId.slice(-8);
    };

    return (
      <div
        key={scanDevice.device.deviceId}
        className="bg-[#1C1C1E] rounded-xl p-4 mb-3"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DeviceIcon
              deviceType={
                isTherapyDevice ? getDeviceDisplayName() : "Pulse Oximeter"
              }
            />
            <div>
              <h3 className="text-white font-medium text-lg">
                {getDeviceDisplayName()}
              </h3>
              <p className="text-gray-400 text-sm">{getDeviceSerial()}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isThisDeviceConnected ? (
              <>
                <div className="flex items-center space-x-2">
                  <span className="font-medium" style={{ color: "#7BD2EC" }}>
                    Connected
                  </span>
                </div>
              </>
            ) : section === "nearby" ? (
              <button
                onClick={async () => {
                  await handleDeviceAction(
                    scanDevice,
                    "connect",
                    deviceCategory
                  );
                }}
                className="font-medium hover:text-blue-300 transition-colors"
                style={{ color: "#05ACD9" }}
              >
                {(isConnectClicked.device === scanDevice &&
                  isConnectClicked.status) ||
                (isPulseOximeterConnectClicked.device === scanDevice &&
                  isPulseOximeterConnectClicked.status)
                  ? "Connecting..."
                  : "Connect"}
              </button>
            ) : (
              <button
                onClick={() =>
                  handleDeviceAction(scanDevice, "connect", deviceCategory)
                }
                style={{
                  height: "28px",
                  fontFamily: "Figtree",
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "14px",
                  textAlign: "right",
                  letterSpacing: "0.374px",
                  color: "#FF9F0A",
                  flex: "none",
                  order: 0,
                  flexGrow: 0,
                }}
              >
                Not Connected
              </button>
            )}
            <IonIcon
              icon={chevronForwardOutline}
              className="w-5 h-5 text-gray-400"
            />
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isConnected) {
      const askForTherapyData = async () => {
        await askForSelectiveData([RequestMessageType.ADHERENCE]);
      };
      askForTherapyData();
    }
  }, [isConnected]);

  useEffect(() => {
    const connectWebDevice = async () => {
      if (Capacitor.getPlatform() === "web") {
        await findAndConnectToDeviceWeb();
      }
    };
    connectWebDevice();
  }, []);

  return (
    <IonPage className="min-h-screen overflow-hidden bg-black text-white">
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="Connect a BiWaze® System"
        showBackButton={true}
        onBackButtonClick={() => router.push("/remote", "back", "replace")}
      />

      {/* Content */}
      <div
        className="px-4 pb-32 h-full overflow-y-auto"
        style={{
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // Better iOS scrolling
        }}
      >
        {/* My Devices Section - Show if any device is connected */}
        {(isConnected || isPulseOximeterConnected) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-400 text-sm font-medium uppercase tracking-wider">
                My Connected BiWaze® System
              </h2>
              {/* <span className="text-gray-400 text-xs">
                {connectionStatus.statusText}
              </span> */}
            </div>

            {/* Connected Therapy Device */}
            {isConnected && device && (
              <div className="bg-[#1C1C1E] rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <DeviceIcon
                      deviceType={
                        parseDeviceString(device.device.name || "").deviceType
                      }
                    />
                    <div>
                      <h3 className="text-white font-medium text-lg">
                        {parseDeviceString(device.device.name || "").deviceType}
                      </h3>
                      <p className="text-gray-400 text-sm">{serialNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      <button
                        className="font-medium"
                        style={{ color: "#7BD2EC" }}
                        onClick={() => handleConnectedDeviceDisconnect()}
                      >
                        Connected
                      </button>
                    </div>
                    <IonIcon
                      icon={chevronForwardOutline}
                      className="w-5 h-5 text-gray-400"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Connected Pulse Oximeter */}
            {isPulseOximeterConnected && pulseOximeterDevice && (
              <div className="bg-[#1C1C1E] rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <DeviceIcon deviceType="Pulse Oximeter" />
                    <div>
                      <h3 className="text-white font-medium text-lg">
                        {pulseOximeterDevice.device.name || "Pulse Oximeter"}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {pulseOximeterDevice.device.deviceId.slice(-8)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2">
                      <button
                        className="font-medium"
                        style={{ color: "#7BD2EC" }}
                        onClick={() => handleConnectedDeviceDisconnect(true)}
                      >
                        Connected
                      </button>
                    </div>
                    <IonIcon
                      icon={chevronForwardOutline}
                      className="w-5 h-5 text-gray-400"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* New Devices Section - Show both therapy and pulse oximeter devices */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <h2 className="text-gray-400 text-sm font-medium uppercase tracking-wider">
              Available BiWaze® Systems with Bluetooth enabled
            </h2>
            <IonSpinner name="lines-small" />
          </div>

          {/* Therapy Devices Subsection */}
          {devices.length > 0 && (
            <div className="mb-4">
              {/* <h3 className="text-gray-500 text-xs font-medium mb-2 uppercase tracking-wider">
                Available BiWaze® Systems
              </h3> */}
              {devices
                .filter(
                  (scanDevice) =>
                    !(
                      isConnected &&
                      device &&
                      scanDevice.device.deviceId === device.device.deviceId
                    )
                )
                .map((scanDevice) => (
                  <DeviceItem
                    key={scanDevice.device.deviceId}
                    device={scanDevice}
                    section="nearby"
                    deviceCategory="therapy"
                  />
                ))}
            </div>
          )}

          {/* Pulse Oximeter Devices Subsection */}
          {pulseOximeterDevices.length > 0 && (
            <div className="mb-4">
              <h3 className="text-gray-500 text-xs font-medium mb-2 uppercase tracking-wider">
                Pulse Oximeters
              </h3>
              {pulseOximeterDevices
                .filter(
                  (scanDevice) =>
                    !(
                      isPulseOximeterConnected &&
                      pulseOximeterDevice &&
                      scanDevice.device.deviceId ===
                        pulseOximeterDevice.device.deviceId
                    )
                )
                .map((scanDevice) => (
                  <DeviceItem
                    key={scanDevice.device.deviceId}
                    device={scanDevice}
                    section="nearby"
                    deviceCategory="pulseOximeter"
                  />
                ))}
            </div>
          )}

          {/* No devices found message */}
          {devices.length === 0 && pulseOximeterDevices.length === 0 && (
            <div className="bg-[#1C1C1E] rounded-xl p-4 mb-3">
              <p className="text-gray-400 text-center">No devices found</p>
            </div>
          )}
        </div>

        {/* Pinned Help Section */}
        <div className="relative bottom-0 left-0 right-0 bg-black">
          <div className="mx-4 mb-4 bg-gray-700 bg-opacity-80 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <IonIcon
                icon={informationCircleOutline}
                className="w-5 h-5 text-gray-300 mt-0.5 flex-shrink-0"
              />
              <div>
                <h3 className="text-white font-medium mb-1">
                  Don&apos;t see your BiWaze® System?
                </h3>
                <p className="text-gray-400 text-sm">
                  Verify that Bluetooth is enabled on both the BiWaze® System
                  and your mobile device
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonPage>
  );
};

export default BluetoothDevicesPage;
